import Lottie from "lottie-react";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdChecklist,
  MdCode,
  MdOndemandVideo,
  MdOutlineGamepad,
  MdOutlinePlayArrow,
  MdOutlineSubtitles,
} from "react-icons/md";

import CheckGreenIcon from "../../../assets/check.png";
import animationData from "../../../assets/json/Comp.json";
import { ReactComponent as Trophies } from "../../../assets/trophies.svg";
import TrophyStar from "../../../assets/trophy-star.png";
import user_placeholder from "../../../assets/user-profile.jpg";
import Button from "../../../components/Button";
import LoadingSpinner from "../../../components/LoadingSpinner";
import WatchVideoModal from "../../../components/modals/common/WatchVideoModal";
import CourseCertificatesModal from "../../../components/modals/student/CourseCertificatesModal";
import SelectStudentAvatarModal from "../../../components/modals/student/SelectStudentAvatarModal";
import StudentChallengeSelectLevelModal from "../../../components/modals/student/StudentChallengeSelectLevelModal";
import TrophiesAndBadgesModal from "../../../components/modals/student/TrophiesAndBadgesModal";
import { USER_TYPE } from "../../../constants/common";
import useAuth from "../../../hooks/useAuth";
import studentServices from "../../../services/studentServices";
import { getLanguage } from "../../../utils/getLanguage";
import { useNavigateWithLang } from "../../../utils/navigation";

const StudentProfile = () => {
  const user = useAuth();
  const isThirdPartyUser = user?.is_b12;
  const { t } = useTranslation();
  const navigate = useNavigateWithLang();
  const lang = getLanguage();
  const studentID = user?.student_id;

  // Refs for modals
  const selectStudentAvatarModalRef = useRef();
  const watchVideoModalRef = useRef();
  const trophiesAndBadgesModalRef = useRef();
  const courseCertificatesModalRef = useRef();
  const challengeSelectLevelModalRef = useRef();

  // States
  const [loading, setLoading] = useState(true);
  const [loadingLessons, setLoadingLessons] = useState(true);
  const [studentDetails, setStudentDetails] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [studentCourse, setStudentCourse] = useState(null);
  const [lineOfCode, setLineOfCode] = useState(0);
  const [studentProjectsStats, setStudentProjectsStats] = useState(null);

  // Modal actions
  const openModal = (ref, ...params) => ref.current?.openModal(...params);

  // Fetch handlers
  const getStudentDetails = async () => {
    try {
      const response = await studentServices.getStudentByID(studentID);
      setStudentDetails(response);
      getStudentLessons();
    } catch (error) {
      console.error("Error getting student details", error);
    } finally {
      setLoading(false);
    }
  };

  const getStudentLessons = async () => {
    try {
      const res = await studentServices.getStudentLessons(studentID);
      setLessons(res.completion);
    } catch (error) {
      console.error("Error fetching lessons:", error);
    } finally {
      setLoadingLessons(false);
    }
  };

  const getStudentCourses = async () => {
    try {
      const response = await studentServices.getStudentCourses(studentID);
      setStudentCourse(response);
    } catch (error) {
      console.error("Error getting student courses", error);
    }
  };

  const getLineOfCode = async () => {
    try {
      const response = await studentServices.getLineOfCode(studentID);
      setLineOfCode(response?.number);
    } catch (error) {
      console.error("Error getting lines of code", error);
    }
  };

  const getStudentProjectsStats = async () => {
    try {
      const response = await studentServices.getStudentProjectsStats();
      setStudentProjectsStats(response);
    } catch (error) {
      console.error("Error getting student projects stats", error);
    }
  };

  const isLessonCompleted = (lesson) => {
    if (lesson.is_challenge && lesson.challenge_details) {
      const { student_total_easy = 0, student_total_hard = 0 } = lesson.challenge_details;
      return student_total_easy > 0 || student_total_hard > 0;
    }

    return lesson.is_completed;
  };

  useEffect(() => {
    getStudentDetails();
    getStudentCourses();
    getLineOfCode();
    getStudentProjectsStats();
  }, [lang]);

  if (loading) return <LoadingSpinner />;

  // Lesson UI handler
  const renderLessonActions = (lesson) => {
    const isForChildren = studentCourse?.for_children;
    const lessonId = lesson?.lesson?.id;

    const buttons = [];

    if (lesson.is_concept_lesson || lesson.is_code) {
      if (lesson.lesson?.has_recap) {
        buttons.push(
          <Button
            key="recap"
            color="white"
            iconLeft={<MdOutlinePlayArrow className="text-blue-500" size={26} />}
            variant="text"
            label={t("recap_video")}
            onClick={() => openModal(watchVideoModalRef, lessonId, "recap")}
          />
        );
      }

      buttons.push(
        <Button
          key="watch"
          color="white"
          iconLeft={<MdOndemandVideo className="text-blue-500" size={26} />}
          variant="text"
          label={t("watch_video")}
          onClick={() => openModal(watchVideoModalRef, lessonId, "lesson")}
        />
      );

      if (lesson.is_completed) {
        buttons.push(
          <Button
            key="questions"
            color="white"
            iconLeft={<MdChecklist className="text-blue-500" size={26} />}
            variant="text"
            label={t("questions_solutions")}
            onClick={() =>
              navigate(
                `/${USER_TYPE.STUDENT.ROLE}/learning-area/lesson-questions/${lessonId}`,
                {},
                { lessonName: lesson?.lesson?.title }
              )
            }
          />
        );

        if (lesson.is_code) {
          buttons.push(
            <Button
              key="code"
              color="white"
              iconLeft={<MdCode className="text-blue-500" size={26} />}
              variant="text"
              label={t("code_solutions")}
              onClick={() =>
                navigate(
                  `/${USER_TYPE.STUDENT.ROLE}/coding-area/code/${lessonId}`,
                  {},
                  { lessonName: lesson?.lesson?.title }
                )
              }
            />
          );
        }
      }
    }

    if (lesson.is_challenge) {
      buttons.push(
        <Button
          key="challenge"
          color="white"
          iconLeft={<MdOutlineSubtitles className="text-blue-500" size={26} />}
          variant="text"
          label={isForChildren ? t("view_challenge") : t("view_all_challenges")}
          onClick={() => {
            if (isForChildren) {
              // Handle children challenge
            } else {
              openModal(challengeSelectLevelModalRef, lessonId, lesson?.lesson?.title);
            }
          }}
        />
      );
    }

    if (lesson.is_project && isForChildren) {
      buttons.push(
        <Button
          key="project"
          color="white"
          iconLeft={<MdOutlineSubtitles className="text-blue-500" size={26} />}
          variant="text"
          label={t("view_project")}
          onClick={() =>
            navigate(
              `/${USER_TYPE.STUDENT.ROLE}/learning-area/levels-project/${lessonId}`,
              {},
              { lessonName: studentCourse?.course }
            )
          }
        />
      );
    }

    if (lesson.is_game && isForChildren) {
      buttons.push(
        <Button
          key="game"
          color="white"
          iconLeft={<MdOutlineGamepad className="text-blue-500" size={26} />}
          variant="text"
          label={t("view_game")}
          onClick={() => navigate(`/${USER_TYPE.STUDENT.ROLE}/learning-area/lesson-game/${lessonId}`)}
        />
      );
    }

    if (lesson.is_graduation_project) {
      buttons.push(
        <Button
          key="graduation"
          color="white"
          iconLeft={<MdCode className="text-blue-500" size={26} />}
          variant="text"
          label={t("view_final_project")}
          onClick={() =>
            navigate(
              `/${USER_TYPE.STUDENT.ROLE}/coding-area/graduation/${lessonId}`,
              {},
              { lessonName: lesson?.lesson?.title }
            )
          }
        />
      );
    }

    return buttons;
  };

  return (
    <div className="min-h-screen space-y-4">
      {/* Header: Avatar + Info */}
      <div className="flex flex-col justify-between gap-6 sm:flex-row">
        <div className="flex flex-1 flex-col items-center gap-4 sm:flex-row sm:items-start">
          <div
            //  onClick={() => openModal(selectStudentAvatarModalRef, studentID)}
            className="cursor-pointer"
          >
            <img
              className="h-24 w-24 rounded-full border-4 border-blue-500 object-cover sm:h-36 sm:w-36"
              src={studentDetails?.image || user_placeholder}
              alt="profile"
            />
          </div>
          <div className="space-y-1 text-center text-blue-500 sm:text-left">
            <h2 className="text-xl font-bold capitalize sm:text-3xl">{studentDetails?.name}</h2>
            {!isThirdPartyUser && <p className="text-sm font-light sm:text-lg">{studentDetails?.username}</p>}
            <p className="text-sm font-bold capitalize sm:text-lg">
              {studentDetails?.classroom?.name} - {studentDetails?.classroom?.grade?.name} -{" "}
              {/* {studentDetails?.classroom?.section} - */}
              {studentDetails?.school?.name}
            </p>
          </div>
        </div>

        <div className="flex flex-row items-center gap-2 py-4 sm:flex-col sm:items-end">
          <button
            disabled
            onClick={() => openModal(courseCertificatesModalRef)}
            className="flex cursor-not-allowed items-center gap-3 rounded-lg bg-orange-300 px-4 py-2 text-sm font-medium text-white opacity-60 shadow"
          >
            <Trophies className="h-4 w-4 opacity-80 sm:h-5 sm:w-5" />
            {t("course_certificates")}
          </button>

          <button
            onClick={() => openModal(trophiesAndBadgesModalRef)}
            className="flex items-center gap-2 rounded-lg bg-blue-500 px-3 py-1 text-xs font-medium text-white shadow sm:px-4 sm:py-2 sm:text-sm"
          >
            <img src={TrophyStar} className="h-4 w-4 sm:h-5 sm:w-5" />
            {t("trophies_badges")}
          </button>
        </div>
      </div>

      {/* Course Summary */}
      <div className="flex w-full flex-col items-center justify-center rounded-full px-4 py-2 text-center sm:flex-row sm:text-left">
        <div className="flex items-center gap-4">
          <div className="w-40 sm:w-72">
            <Lottie animationData={animationData} autoplay loop={false} />
          </div>
          <span className="text-xl font-bold text-blue-500 sm:text-3xl">{studentCourse?.course}</span>
        </div>
      </div>

      {/* Stats */}
      <div className="flex flex-wrap items-center justify-center gap-4 rounded-3xl border border-blue-500 bg-white px-4 py-4 text-blue-500 sm:justify-between sm:gap-6 sm:px-10 sm:py-8">
        {studentCourse?.for_children ? (
          <div className="text-sm font-light sm:text-lg">
            💡 {t("projects")}:{" "}
            <span className="font-bold text-orange-500">
              {(studentProjectsStats?.approved_challenges || 0) +
                (studentProjectsStats?.pending_challenges || 0) +
                (studentProjectsStats?.rejected_challenges || 0)}
            </span>
          </div>
        ) : (
          <div className="text-sm font-light sm:text-lg">
            🧠 {t("challenges")}:{" "}
            <span className="font-bold text-yellow-500">
              {(studentProjectsStats?.approved_challenges || 0) +
                (studentProjectsStats?.pending_challenges || 0) +
                (studentProjectsStats?.rejected_challenges || 0)}
            </span>
          </div>
        )}
        {/* <div className="text-lg font-light">
          🏠 {t("homeworks")}: <span className="font-bold text-orange-500">3</span>
        </div> */}
        <div className="text-sm font-light sm:text-lg">
          🧮 {t("total_points_score")}: <span className="font-bold">{studentDetails?.total_points}</span>
        </div>
        {!studentCourse?.for_children && (
          <div className="text-sm font-light sm:text-lg">
            📨 {t("line_of_code")}: <span className="font-bold">{lineOfCode}</span>
          </div>
        )}
      </div>

      {/* Lessons */}
      {loadingLessons ? (
        <LoadingSpinner />
      ) : (
        <div className="space-y-2">
          {lessons.map((lesson, index) => {
            const numberLabel = lesson.is_ordered ? `${index + 1}: ` : "";
            const prevLesson = lessons?.[index - 1];
            const isLocked = lesson.is_locked && lesson.is_ordered;
            const isLockedUnordered = lesson.is_locked && !lesson.is_ordered && prevLesson?.is_locked;
            const lessonClasses = isLocked
              ? "bg-[#1C3674] text-white cursor-not-allowed"
              : isLockedUnordered
                ? "bg-[#1C3674] text-white cursor-not-allowed"
                : "bg-white text-blue-500";

            return (
              <div
                key={lesson?.lesson?.id}
                className={`flex flex-col items-center gap-2 rounded-3xl px-4 py-3 shadow sm:flex-row sm:justify-between sm:px-5 sm:py-4 ${lessonClasses}`}
              >
                <div className="relative flex items-center gap-2 text-sm font-semibold sm:text-lg">
                  {isLessonCompleted(lesson) && (
                    <img
                      className="absolute h-5 w-5 sm:h-6 sm:w-6 ltr:left-0 sm:ltr:left-[-30px] rtl:right-0 sm:rtl:right-[-30px]"
                      src={CheckGreenIcon}
                      alt="Completed"
                    />
                  )}
                  <span className="ml-6 sm:ml-0">
                    {numberLabel}
                    {lesson?.lesson?.title}
                  </span>
                </div>
                {!isLocked && isLessonCompleted(lesson) && (
                  <div className="flex flex-wrap justify-center gap-2 sm:justify-end">
                    {renderLessonActions(lesson)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      <WatchVideoModal ref={watchVideoModalRef} />
      <TrophiesAndBadgesModal ref={trophiesAndBadgesModalRef} />
      <CourseCertificatesModal ref={courseCertificatesModalRef} />
      <StudentChallengeSelectLevelModal ref={challengeSelectLevelModalRef} />
      <SelectStudentAvatarModal ref={selectStudentAvatarModalRef} />
    </div>
  );
};

export default StudentProfile;
